import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, messageErreur } from '../../lib/api'
import type { CategorieDonnee, Donnee } from '../../lib/types'
import { Champ as ChampUI, Message, Modale } from '../base'
import { CHAMPS, TITRES_FORMULAIRE } from './champs'
import * as f from '../../lib/format'

/**
 * Saisie et correction d'une donnée médicale. À la correction, une raison peut
 * être écrite : elle est rangée avec la version précédente.
 */
export default function FormulaireDonnee({
  patientId,
  categorie,
  donnee,
  valeursInitiales,
  fermer,
}: {
  patientId: string
  categorie: CategorieDonnee
  donnee?: Donnee
  /** Valeurs posées d'avance, par exemple le domaine d'une donnée spécifique. */
  valeursInitiales?: Record<string, unknown>
  fermer: () => void
}) {
  const client = useQueryClient()
  const champs = CHAMPS[categorie] ?? []
  const titres = TITRES_FORMULAIRE[categorie]

  const [valeurs, setValeurs] = useState<Record<string, unknown>>(() => {
    const depart: Record<string, unknown> = {}
    for (const champ of champs) {
      const existante = donnee?.detail?.[champ.cle]
      if (champ.type === 'date') depart[champ.cle] = f.pourChampDate(existante as string | null)
      else if (champ.type === 'booleen') depart[champ.cle] = existante ?? true
      else if (champ.type === 'choix')
        depart[champ.cle] = (existante as string) ?? Object.keys(champ.options ?? {})[0] ?? ''
      else depart[champ.cle] = (existante as string) ?? ''
    }
    return { ...depart, ...(valeursInitiales ?? {}) }
  })
  const [raison, setRaison] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)

  const enregistrer = useMutation({
    mutationFn: () =>
      donnee
        ? api(`/api/donnees/${donnee.id}`, {
            methode: 'PATCH',
            corps: { detail: valeurs, raison: raison || undefined },
          })
        : api(`/api/patients/${patientId}/donnees`, {
            methode: 'POST',
            corps: { categorie, detail: valeurs },
          }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['dossier', patientId] })
      client.invalidateQueries({ queryKey: ['accueil'] })
      fermer()
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  return (
    <Modale
      titre={donnee ? (titres?.edition ?? 'Corriger') : (titres?.ajout ?? 'Ajouter')}
      sousTitre={
        donnee
          ? `Version ${donnee.version} actuellement. L'ancienne valeur restera consultable.`
          : 'Saisie manuelle : elle sera enregistrée à ton nom, sans source documentaire.'
      }
      fermer={fermer}
      piedDePage={
        <>
          <button className="bouton-discret" onClick={fermer}>
            Annuler
          </button>
          <button
            className="bouton-primaire"
            onClick={() => enregistrer.mutate()}
            disabled={enregistrer.isPending}
          >
            {donnee ? 'Enregistrer la correction' : 'Ajouter au dossier'}
          </button>
        </>
      }
    >
      <form
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault()
          enregistrer.mutate()
        }}
      >
        {champs.map((champ) => (
          <ChampUI
            key={champ.cle}
            etiquette={champ.etiquette + (champ.requis ? ' *' : '')}
            aide={champ.aide}
            className={champ.pleineLargeur || champ.type === 'zone' ? 'sm:col-span-2' : ''}
          >
            {champ.type === 'zone' ? (
              <textarea
                className="champ min-h-[76px]"
                value={String(valeurs[champ.cle] ?? '')}
                onChange={(e) => setValeurs((v) => ({ ...v, [champ.cle]: e.target.value }))}
              />
            ) : champ.type === 'choix' ? (
              <select
                className="champ"
                value={String(valeurs[champ.cle] ?? '')}
                onChange={(e) => setValeurs((v) => ({ ...v, [champ.cle]: e.target.value }))}
              >
                {Object.entries(champ.options ?? {}).map(([cle, libelle]) => (
                  <option key={cle} value={cle}>
                    {libelle}
                  </option>
                ))}
              </select>
            ) : champ.type === 'booleen' ? (
              <label className="flex items-center gap-2 py-2 text-[14px]">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--color-bleu-600)]"
                  checked={Boolean(valeurs[champ.cle])}
                  onChange={(e) => setValeurs((v) => ({ ...v, [champ.cle]: e.target.checked }))}
                />
                Oui
              </label>
            ) : (
              <>
                <input
                  className="champ"
                  type={champ.type === 'date' ? 'date' : 'text'}
                  required={champ.requis}
                  list={champ.suggestions ? `suggestions-${champ.cle}` : undefined}
                  value={String(valeurs[champ.cle] ?? '')}
                  onChange={(e) => setValeurs((v) => ({ ...v, [champ.cle]: e.target.value }))}
                />
                {champ.suggestions && (
                  <datalist id={`suggestions-${champ.cle}`}>
                    {champ.suggestions.map((suggestion) => (
                      <option key={suggestion} value={suggestion} />
                    ))}
                  </datalist>
                )}
              </>
            )}
          </ChampUI>
        ))}

        {donnee && (
          <ChampUI
            etiquette="Raison de la correction"
            aide="Rangée avec l'ancienne valeur dans l'historique."
            className="sm:col-span-2"
          >
            <input
              className="champ"
              value={raison}
              onChange={(e) => setRaison(e.target.value)}
              placeholder="Dosage corrigé d'après le compte rendu du 12 mars"
            />
          </ChampUI>
        )}

        {erreur && (
          <div className="sm:col-span-2">
            <Message ton="critique">{erreur}</Message>
          </div>
        )}
      </form>
    </Modale>
  )
}
